import { useState, type FormEvent } from 'react';
import { CheckCircle2, Mail, School, Send, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const benefits = [
  'Tell us which board to prioritize next',
  'Get notified when your curriculum opens',
  'Help shape early pilot access for your school',
];

const roles = [
  'Teacher',
  'School leader',
  'College faculty',
  'Tutor',
  'EdTech team',
  'Other',
];

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

export default function BoardWaitlistSection() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [board, setBoard] = useState('');
  const [role, setRole] = useState('Teacher');
  const [organization, setOrganization] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitState('submitting');
    setMessage('');

    const normalizedEmail = email.trim().toLowerCase();
    const hasValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);

    if (!fullName.trim() || !hasValidEmail || !board.trim()) {
      setSubmitState('error');
      setMessage('Enter your name, a valid email, and the board you need.');
      return;
    }

    const { error } = await supabase.from('board_waitlist').insert({
      full_name: fullName.trim(),
      email: normalizedEmail,
      board: board.trim(),
      role,
      organization: organization.trim(),
      source: 'landing_page',
    });

    if (error) {
      if (error.code === '23505') {
        setSubmitState('success');
        setMessage('You are already on the waitlist for this board.');
        return;
      }

      setSubmitState('error');
      setMessage(
        error.code === '42P01'
          ? 'The waitlist database is not deployed yet. Please ask the admin to apply the latest Supabase migration.'
          : error.message || 'Could not join the waitlist right now. Please try again.'
      );
      return;
    }

    setSubmitState('success');
    setMessage('You are on the waitlist. We will use this demand to prioritize upcoming boards.');
    setFullName('');
    setEmail('');
    setBoard('');
    setRole('Teacher');
    setOrganization('');
  };

  return (
    <section id="board-waitlist" className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-[0.95fr_1.05fr] gap-10 lg:gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold uppercase tracking-wider mb-5">
            Other boards coming soon
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Teaching outside NCERT?
            <span className="block text-teal-600">Tell us your board.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Xed21 is focused on NCERT first. If you teach ICSE, a State Board,
            IB, IGCSE, or another curriculum, join the waitlist so we can
            prioritize launches by real educator demand.
          </p>

          <div className="mt-8 space-y-4">
            {benefits.map((benefit) => (
              <div key={benefit} className="flex items-start gap-3">
                <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-teal-50">
                  <CheckCircle2 className="h-4 w-4 text-teal-700" />
                </div>
                <p className="text-sm font-medium text-slate-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8 shadow-sm">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Full name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-3 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                  placeholder="Your name"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-3 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                  placeholder="you@example.com"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Board needed</label>
              <div className="relative">
                <School className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={board}
                  onChange={(event) => setBoard(event.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-3 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                  placeholder="e.g. Maharashtra State Board"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Role</label>
              <select
                value={role}
                onChange={(event) => setRole(event.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition-all focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
              >
                {roles.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 space-y-1.5">
            <label className="block text-sm font-medium text-slate-700">
              School or organization <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              value={organization}
              onChange={(event) => setOrganization(event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
              placeholder="e.g. Delhi Public School"
            />
          </div>

          <button
            type="submit"
            disabled={submitState === 'submitting'}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal-700 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-teal-700/20 transition-all hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitState === 'submitting' ? (
              <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Join Board Waitlist
          </button>

          {message && (
            <div
              className={`mt-4 rounded-xl border p-3 text-sm ${
                submitState === 'success'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-rose-200 bg-rose-50 text-rose-700'
              }`}
              aria-live="polite"
            >
              {message}
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
